import { useRef } from 'react';

import { Alert } from 'cpool-components/general/Alert';
import { AlertState, AlertVariant } from 'cpool-components/general/Alert/types';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES } from 'cpool-constants';
import { Trans, useTranslation } from 'react-i18next';
import { LuCheck, LuX } from 'react-icons/lu';

import { useUserDisplayName } from './hooks/useUserDisplayName';
import DialogBox from '~/components/DialogBox';
import Spinner from '~/components/icons/Spinner';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { dateFormat } from '~/lib/i18n';
import type { EndRepresentation } from '~/utils/representation';

type Props = {
    appointerName?: string;
    isPending?: boolean;
    onClose: () => void;
    onConfirm: (representation: EndRepresentation) => void;
    open: boolean;
    representation: EndRepresentation;
    showLeadTrackAlert?: boolean;
};

export const EndRepresentationDialog: React.FC<Props> = ({
    appointerName,
    isPending = false,
    onClose,
    onConfirm,
    open,
    representation,
    showLeadTrackAlert = true,
}) => {
    const { t } = useTranslation();
    const initialFocusRef = useRef<HTMLDivElement>(null);
    const { permissions } = useAclPermissions();

    const appointerId =
        representation.kind === 'delegation' ? representation.appointedById : representation.appointerId;
    const resolvedAppointerName = useUserDisplayName(appointerId) ?? appointerName;
    const delegateName = useUserDisplayName(representation.delegateId);

    const from =
        representation.kind === 'delegation'
            ? dateFormat(representation.appointedAt)
            : dateFormat(representation.absenceFrom);
    const until =
        representation.kind === 'delegation'
            ? t('TOP_HEADER.REPRESENTATION.UNLIMITED')
            : dateFormat(representation.absenceUntil);

    const canConfirm =
        representation.kind === 'delegation'
            ? permissions.commands[AGGREGATE_NAMES.USER].revokeDelegate
            : permissions.commands[AGGREGATE_NAMES.USER].deleteAdviserAbsence;

    const isLoading = !resolvedAppointerName || !delegateName;

    return (
        <DialogBox
            title={t('DASHBOARD.ADVISER_ABSENCE.END_TITLE')}
            open={open}
            onClose={onClose}
            initialFocusRef={initialFocusRef}
            panelClassName="max-w-[512px] rounded-lg p-6"
            dataTestId="end-representation-dialog"
        >
            <div className="flex flex-col gap-6">
                <div className="text-sm text-midnight">
                    {isLoading ? (
                        <Spinner className="max-w-6" />
                    ) : (
                        <Trans
                            i18nKey="DASHBOARD.ADVISER_ABSENCE.END_TEXT"
                            values={{
                                appointerName: resolvedAppointerName,
                                delegateName,
                                from,
                                until,
                            }}
                            components={{
                                bold: <span className="font-medium" />,
                            }}
                        />
                    )}
                </div>

                {showLeadTrackAlert ? (
                    <Alert state={AlertState.ERROR} variant={AlertVariant.PRIMARY} hideIcon>
                        {t('DASHBOARD.ADVISER_ABSENCE.END_ALERT')}
                    </Alert>
                ) : null}

                <div className="flex justify-end gap-2">
                    <Button variant={ButtonVariant.SECONDARY} iconLeft={<LuX />} onClick={onClose} disabled={isPending}>
                        {t('BUTTONS.CANCEL')}
                    </Button>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        iconLeft={<LuCheck />}
                        onClick={() => onConfirm(representation)}
                        disabled={!canConfirm || isPending || isLoading}
                    >
                        {t('DASHBOARD.ADVISER_ABSENCE.END_BUTTON')}
                    </Button>
                </div>
            </div>
        </DialogBox>
    );
};
